import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, Plus, Trash2 } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import type { Payment, PaymentStatus } from "@/types";
import { useClients } from "@/features/clients/clients.hooks";
import { usePayments, useCreatePayment, useArchivePayment } from "./payments.hooks";
import type { PaymentInput } from "./payments.adapter";
import { PaymentFormModal } from "./PaymentFormModal";
import { getPaymentDetailPath, getPaymentNotePeriod } from "./payment-details.logic";

type StatusFilter = "all" | PaymentStatus;

export function PaymentsPage() {
  const { t } = useTranslation();
  const { PAYMENT_STATUS, PAYMENT_TYPE, PAYMENT_METHOD } = useLabels();
  const navigate = useNavigate();
  const filters: FilterOption<StatusFilter>[] = [
    { value: "all", label: t("payments.filters.all") },
    { value: "RECEIVED", label: t("payments.filters.received") },
    { value: "PENDING", label: t("payments.filters.pending") },
    { value: "OVERDUE", label: t("payments.filters.overdue") },
  ];
  const { payments, isLoading, isError, refetch } = usePayments();
  const createPayment = useCreatePayment();
  const archivePayment = useArchivePayment();
  const { clients } = useClients();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [toDelete, setToDelete] = useState<Payment | undefined>();
  const [addOpen, setAddOpen] = useState(false);

  const visible = useMemo(
    () => (filter === "all" ? payments : payments.filter((payment) => payment.status === filter)),
    [payments, filter],
  );

  const stats = useMemo(() => {
    const received = payments.filter((payment) => payment.status === "RECEIVED").reduce((sum, payment) => sum + payment.amount, 0);
    const pending = payments.filter((payment) => payment.status === "PENDING").reduce((sum, payment) => sum + payment.amount, 0);
    const overdue = payments.filter((payment) => payment.status === "OVERDUE").reduce((sum, payment) => sum + payment.amount, 0);
    const total = payments.reduce((sum, payment) => sum + payment.amount, 0);
    return { received, pending, overdue, total };
  }, [payments]);

  const onCreate = async (input: PaymentInput) => {
    try {
      await createPayment.mutateAsync(input);
      toast.success(t("payments.created"));
      setAddOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("payments.createFailed")));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toDelete) return;
    try {
      await archivePayment.mutateAsync(toDelete.id);
      toast.success(t("payments.archived"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("payments.archiveFailed")));
    } finally {
      setToDelete(undefined);
    }
  };

  const columns: Column<Payment>[] = [
    {
      key: "client",
      header: t("payments.columns.client"),
      cell: (payment) => (
        <button
          type="button"
          className="flex max-w-[210px] items-center gap-2 rounded-sm text-left font-semibold text-text transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          onClick={(event) => {
            event.stopPropagation();
            navigate(getPaymentDetailPath(payment.id));
          }}
          title={t("payments.details.view")}
        >
          <Avatar name={payment.clientName} size={24} />
          <span className="truncate">{payment.clientName}</span>
        </button>
      ),
    },
    {
      key: "type",
      header: t("payments.columns.type"),
      cell: (payment) => <Badge tone={PAYMENT_TYPE[payment.type].tone}>{PAYMENT_TYPE[payment.type].label}</Badge>,
    },
    {
      key: "method",
      header: t("payments.columns.method"),
      cell: (payment) =>
        payment.method ? <Badge>{PAYMENT_METHOD[payment.method].label}</Badge> : <span className="text-text3">{t("payments.emptyValue")}</span>,
    },
    {
      key: "note",
      header: t("payments.columns.notePeriod"),
      cell: (payment) => {
        const notePeriod = getPaymentNotePeriod(payment);
        return (
          <div className="min-w-[140px]">
            <div className="text-text2">{notePeriod.primary}</div>
            {notePeriod.secondary && <div className="mt-0.5 text-[11px] text-text3">{notePeriod.secondary}</div>}
          </div>
        );
      },
    },
    {
      key: "amount",
      header: t("payments.columns.amount"),
      align: "right",
      cell: (payment) => <span className="mono">{formatDZD(payment.amount)}</span>,
    },
    {
      key: "status",
      header: t("payments.columns.status"),
      cell: (payment) => (
        <Badge tone={PAYMENT_STATUS[payment.status].tone} dot>
          {PAYMENT_STATUS[payment.status].label}
        </Badge>
      ),
    },
    { key: "date", header: t("payments.columns.date"), align: "right", cell: (payment) => <span className="text-text3">{payment.date}</span> },
    {
      key: "actions",
      header: t("common.actions"),
      align: "right",
      cell: (payment) => (
        <div className="flex justify-end gap-1.5">
          <Button
            variant="ghost"
            size="xs"
            onClick={(event) => {
              event.stopPropagation();
              navigate(getPaymentDetailPath(payment.id));
            }}
            title={t("payments.details.view")}
            aria-label={t("payments.details.view")}
          >
            <Eye className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            className="text-danger"
            onClick={(event) => {
              event.stopPropagation();
              setToDelete(payment);
            }}
            title={t("common.archive")}
            aria-label={t("common.archive")}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t("payments.title")}
        subtitle={t("payments.subtitle", { count: payments.length, received: formatDZD(stats.received) })}
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-3 w-3" /> {t("payments.add")}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <KPICard label={t("payments.kpi.received")} value={<span className="text-success">{formatDZD(stats.received)}</span>} />
        <KPICard label={t("payments.kpi.pending")} value={<span className="text-warning">{formatDZD(stats.pending)}</span>} />
        <KPICard label={t("payments.kpi.overdue")} value={<span className="text-danger">{formatDZD(stats.overdue)}</span>} />
        <KPICard label={t("payments.kpi.total")} value={formatDZD(stats.total)} />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} message={t("dashboard.banner.apiDown")} actionLabel={t("states.retry")} />}

      <div className="mb-3.5">
        <SegmentedFilter options={filters} value={filter} onChange={setFilter} />
      </div>

      <Card>
        {isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <DataTable
            columns={columns}
            rows={visible}
            rowKey={(payment) => payment.id}
            onRowClick={(payment) => navigate(getPaymentDetailPath(payment.id))}
            empty={{ title: t("states.empty") }}
          />
        )}
      </Card>

      <PaymentFormModal open={addOpen} onClose={() => setAddOpen(false)} onSave={onCreate} clients={clients} saving={createPayment.isPending} />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title={t("payments.archiveTitle")}
        message={t("payments.archiveMessage", {
          client: toDelete?.clientName,
          amount: toDelete ? formatDZD(toDelete.amount) : "",
        })}
        confirmLabel={t("common.archive")}
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}
